import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { SubitemTemplateService } from "../services/subitemTemplateService";
import { TemplateCategoryService } from "../services/templateCategoryService";
import LoadingSpinner from "./LoadingSpinner";
import ErrorMessage from "./ErrorMessage";
import { Trash2, Filter } from "lucide-react";
import {
  Table,
  Button,
  SearchBar,
  ConfirmDialog,
  Popover,
  Dropdown,
  FilterChip,
  MultiSelect,
} from "./ui";

const SubitemTemplateTable = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("phase");
  const [sortDirection, setSortDirection] = useState("asc");
  const saveTimeoutRef = useRef(null);
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    template: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    phase: "",
    templateType: "",
  });

  // Common phases used in the system - matches database phase_enum
  const commonPhases = useMemo(
    () => [
      "00-Startup",
      "01-Data Prep",
      "02-Utilities",
      "03-Discrepancy Check",
      "04-Erosion/Pond",
      "05-Modeling",
      "06-Double Check",
      "07-Corrections",
      "08-Review",
      "09-Ready to Export",
    ],
    [],
  );

  // Categories loaded from database
  const [categories, setCategories] = useState([]);

  // Build filter options from database categories
  const commonFilters = useMemo(() => {
    if (categories.length === 0) {
      return [];
    }

    const typeToPrefix = {
      base: "00",
      deliverable: "01",
      project_type: "02",
      add_on: "02",
      file_format: "03",
      unit_system: null,
    };

    return categories.map((cat) => {
      const prefix = typeToPrefix[cat.categoryType];
      if (prefix) {
        return `${prefix}-${cat.name}`;
      }
      return cat.name;
    });
  }, [categories]);

  useEffect(() => {
    loadTemplates();
  }, []);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters.phase, filters.templateType]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load both templates and categories in parallel
      const [templatesData, categoriesData] = await Promise.all([
        SubitemTemplateService.getAllSubitemTemplates(),
        TemplateCategoryService.getCategories(),
      ]);

      setTemplates(templatesData);
      setCategories(categoriesData);
    } catch (err) {
      console.error("Error loading templates:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedTemplates = useMemo(() => {
    let filtered = templates;

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (template) =>
          (template.name &&
            template.name.toLowerCase().includes(searchLower)) ||
          (template.phase &&
            template.phase.toLowerCase().includes(searchLower)) ||
          (template.templateFilters &&
            template.templateFilters.some((f) =>
              f.toLowerCase().includes(searchLower),
            )),
      );
    }

    // Apply phase filter
    if (filters.phase) {
      filtered = filtered.filter(
        (template) => template.phase === filters.phase,
      );
    }

    // Apply template type filter
    if (filters.templateType) {
      filtered = filtered.filter((template) => {
        const hasRevision = template.templateFilters?.some((f) =>
          f.includes("Revision"),
        );
        const hasOther = template.templateFilters?.some(
          (f) => !f.includes("Revision"),
        );

        if (filters.templateType === "base") {
          return hasOther && !hasRevision;
        } else if (filters.templateType === "revision") {
          return hasRevision && !hasOther;
        } else if (filters.templateType === "both") {
          return hasRevision && hasOther;
        }
        return true;
      });
    }

    // Sort by phase alphabetically, then by order number
    return filtered.sort((a, b) => {
      // First sort by phase
      const phaseA = a.phase || "";
      const phaseB = b.phase || "";
      const phaseCompare = phaseA.localeCompare(phaseB);

      if (phaseCompare !== 0) {
        return phaseCompare;
      }

      // If phases are the same, sort by order number
      const orderA = a.order || 0;
      const orderB = b.order || 0;
      return orderA - orderB;
    });
  }, [templates, searchTerm, filters]);

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Handle manual column resize - auto-disable auto-resize
  const handleManualResize = useCallback(() => {
    if (autoResizeEnabled) {
      setAutoResizeEnabled(false);
    }
  }, [autoResizeEnabled]);

  // Handle page change
  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage >= 1 && newPage <= totalPages) {
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [totalPages],
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  const handleNewTemplate = async () => {
    try {
      const newTemplate = {
        name: "New Template",
        phase: "01-Data Prep",
        order: 1000,
        revisionVersion: "",
        templateFilters: ["01-Surface Design"],
      };
      await SubitemTemplateService.createSubitemTemplate(newTemplate);
      await loadTemplates();
    } catch (err) {
      console.error("Error creating template:", err);
      setError(err.message);
    }
  };

  const handleFieldChange = useCallback((templateId, field, value) => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Update state immediately for responsive UI
    setTemplates((prev) => {
      const updated = prev.map((t) => {
        if (t.id === templateId) {
          return { ...t, [field]: value };
        }
        return t;
      });

      // Schedule save after state update
      const templateToSave = updated.find((t) => t.id === templateId);
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          if (templateToSave) {
            await SubitemTemplateService.updateSubitemTemplate(
              templateId,
              templateToSave,
            );
          }
        } catch (err) {
          console.error("Error auto-saving template:", err);
          setError(err.message);
        }
      }, 500);

      return updated;
    });
  }, []);

  const handleDeleteTemplate = (template) => {
    setDeleteConfirm({ isOpen: true, template });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.template) return;

    try {
      setLoading(true);
      await SubitemTemplateService.deleteSubitemTemplate(
        deleteConfirm.template.id,
      );
      await loadTemplates();
      setDeleteConfirm({ isOpen: false, template: null });
    } catch (err) {
      console.error("Error deleting template:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Pagination: Calculate total pages and get current page data
  const displayedTemplates = useMemo(() => {
    const totalFiltered = filteredAndSortedTemplates.length;
    const calculatedTotalPages =
      pageSize === 999999 ? 1 : Math.ceil(totalFiltered / pageSize);

    // Update total pages if it changed
    if (calculatedTotalPages !== totalPages) {
      setTotalPages(calculatedTotalPages);
    }

    // Reset to page 1 if current page is beyond total pages
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }

    // Return all if "All" is selected
    if (pageSize === 999999) {
      return filteredAndSortedTemplates;
    }

    // Return current page slice
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedTemplates.slice(startIndex, endIndex);
  }, [filteredAndSortedTemplates, currentPage, pageSize, totalPages]);

  // Define columns for Table component
  const columns = useMemo(
    () => [
      {
        id: "name",
        label: "Base Subitem",
        field: "name",
        width: 250,
        minWidth: 150,
        sortable: true,
        resizable: true,
        contentType: "custom",
        render: (value, row) => {
          const templateType =
            row.templateFilters?.some((f) => f.includes("Revision")) &&
            row.templateFilters?.some((f) => !f.includes("Revision"))
              ? "both"
              : row.templateFilters?.some((f) => f.includes("Revision"))
                ? "revision"
                : "base";

          return (
            <input
              type="text"
              value={value || ""}
              onChange={(e) =>
                handleFieldChange(row.id, "name", e.target.value)
              }
              placeholder={
                templateType === "revision"
                  ? 'Set type to "Both" to edit base'
                  : "Base subitem name"
              }
              disabled={templateType === "revision"}
              style={{
                width: "100%",
                padding: "6px 4px 6px 0", // No left padding - cell provides it (20px)
                border: "1px solid transparent",
                borderRadius: "4px",
                fontSize: "14px",
                background: "transparent",
                transition: "all 0.2s ease",
                color: "var(--gray-50)",
                cursor: templateType === "revision" ? "not-allowed" : "text",
                opacity: templateType === "revision" ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (templateType !== "revision") {
                  e.currentTarget.style.background = "var(--dark-elevated)";
                  e.currentTarget.style.borderColor = "var(--gray-600)";
                }
              }}
              onMouseLeave={(e) => {
                if (
                  templateType !== "revision" &&
                  document.activeElement !== e.currentTarget
                ) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderColor = "transparent";
                }
              }}
              onFocus={(e) => {
                e.currentTarget.style.background = "var(--dark-bg)";
                e.currentTarget.style.borderColor = "var(--cta-blue)";
                e.currentTarget.style.boxShadow =
                  "0 0 0 2px rgba(59, 130, 246, 0.2)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "transparent";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          );
        },
      },
      {
        id: "revisionVersion",
        label: "Revision Subitem",
        field: "revisionVersion",
        width: 250,
        minWidth: 150,
        sortable: true,
        resizable: true,
        contentType: "custom",
        render: (value, row) => {
          const templateType =
            row.templateFilters?.some((f) => f.includes("Revision")) &&
            row.templateFilters?.some((f) => !f.includes("Revision"))
              ? "both"
              : row.templateFilters?.some((f) => f.includes("Revision"))
                ? "revision"
                : "base";

          return (
            <input
              type="text"
              value={value || ""}
              onChange={(e) =>
                handleFieldChange(row.id, "revisionVersion", e.target.value)
              }
              placeholder={
                templateType === "base"
                  ? 'Set type to "Both" or "Revision" to edit'
                  : "Revision subitem name"
              }
              disabled={templateType === "base"}
              style={{
                width: "100%",
                padding: "6px 4px 6px 0", // No left padding - cell provides it (20px)
                border: "1px solid transparent",
                borderRadius: "4px",
                fontSize: "14px",
                background: "transparent",
                transition: "all 0.2s ease",
                color: "var(--gray-50)",
                cursor: templateType === "base" ? "not-allowed" : "text",
                opacity: templateType === "base" ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (templateType !== "base") {
                  e.currentTarget.style.background = "var(--dark-elevated)";
                  e.currentTarget.style.borderColor = "var(--gray-600)";
                }
              }}
              onMouseLeave={(e) => {
                if (
                  templateType !== "base" &&
                  document.activeElement !== e.currentTarget
                ) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderColor = "transparent";
                }
              }}
              onFocus={(e) => {
                e.currentTarget.style.background = "var(--dark-bg)";
                e.currentTarget.style.borderColor = "var(--cta-blue)";
                e.currentTarget.style.boxShadow =
                  "0 0 0 2px rgba(59, 130, 246, 0.2)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "transparent";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          );
        },
      },
      {
        id: "phase",
        label: "Phase",
        field: "phase",
        width: 180,
        minWidth: 180,
        maxWidth: 220,
        sortable: true,
        resizable: true,
        contentType: "custom",
        render: (value, row) => (
          <select
            value={value || ""}
            onChange={(e) => handleFieldChange(row.id, "phase", e.target.value)}
            style={{
              width: "100%",
              padding: "6px 4px 6px 0", // No left padding - cell provides it (20px)
              border: "1px solid transparent",
              borderRadius: "4px",
              fontSize: "14px",
              background: "transparent",
              transition: "all 0.2s ease",
              color: "var(--gray-50)",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--dark-elevated)";
              e.currentTarget.style.borderColor = "var(--gray-600)";
            }}
            onMouseLeave={(e) => {
              if (document.activeElement !== e.currentTarget) {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "transparent";
              }
            }}
            onFocus={(e) => {
              e.currentTarget.style.background = "var(--dark-bg)";
              e.currentTarget.style.borderColor = "var(--cta-blue)";
              e.currentTarget.style.boxShadow =
                "0 0 0 2px rgba(59, 130, 246, 0.2)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "transparent";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <option value="">Select Phase</option>
            {commonPhases.map((phase) => (
              <option key={phase} value={phase}>
                {phase}
              </option>
            ))}
          </select>
        ),
      },
      {
        id: "order",
        label: "Order",
        field: "order",
        width: 120,
        minWidth: 120, // Increased to account for input padding + borders + spinner
        maxWidth: 140,
        sortable: true,
        resizable: true,
        align: "center",
        contentType: "icon", // Use 'icon' contentType for extra padding (40px base + 20px extra)
        render: (value, row) => (
          <input
            type="number"
            value={value || ""}
            onChange={(e) => handleFieldChange(row.id, "order", e.target.value)}
            placeholder="Order"
            style={{
              width: "100%",
              padding: "6px 4px 6px 0", // No left padding - cell provides it (20px)
              border: "1px solid transparent",
              borderRadius: "4px",
              fontSize: "14px",
              background: "transparent",
              transition: "all 0.2s ease",
              color: "var(--gray-50)",
              textAlign: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--dark-elevated)";
              e.currentTarget.style.borderColor = "var(--gray-600)";
            }}
            onMouseLeave={(e) => {
              if (document.activeElement !== e.currentTarget) {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "transparent";
              }
            }}
            onFocus={(e) => {
              e.currentTarget.style.background = "var(--dark-bg)";
              e.currentTarget.style.borderColor = "var(--cta-blue)";
              e.currentTarget.style.boxShadow =
                "0 0 0 2px rgba(59, 130, 246, 0.2)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "transparent";
              e.currentTarget.style.boxShadow = "none";
            }}
          />
        ),
      },
      {
        id: "templateType",
        label: "Template Type",
        field: "templateFilters",
        width: 130,
        minWidth: 120,
        maxWidth: 150,
        sortable: false,
        resizable: true,
        contentType: "custom",
        render: (value, row) => {
          const templateType =
            row.templateFilters?.some((f) => f.includes("Revision")) &&
            row.templateFilters?.some((f) => !f.includes("Revision"))
              ? "both"
              : row.templateFilters?.some((f) => f.includes("Revision"))
                ? "revision"
                : "base";

          return (
            <select
              value={templateType}
              onChange={(e) => {
                const newType = e.target.value;
                let filters = [];
                if (newType === "both") {
                  filters = ["01-Surface Design", "04-Revision"];
                } else if (newType === "revision") {
                  filters = ["04-Revision"];
                } else {
                  filters = ["01-Surface Design"];
                }
                handleFieldChange(row.id, "templateFilters", filters);
              }}
              style={{
                width: "100%",
                padding: "6px 4px 6px 0", // No left padding - cell provides it (20px)
                border: "1px solid transparent",
                borderRadius: "4px",
                fontSize: "14px",
                background: "transparent",
                transition: "all 0.2s ease",
                color: "var(--gray-50)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--dark-elevated)";
                e.currentTarget.style.borderColor = "var(--gray-600)";
              }}
              onMouseLeave={(e) => {
                if (document.activeElement !== e.currentTarget) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderColor = "transparent";
                }
              }}
              onFocus={(e) => {
                e.currentTarget.style.background = "var(--dark-bg)";
                e.currentTarget.style.borderColor = "var(--cta-blue)";
                e.currentTarget.style.boxShadow =
                  "0 0 0 2px rgba(59, 130, 246, 0.2)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "transparent";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <option value="base">Base</option>
              <option value="revision">Revision</option>
              <option value="both">Both</option>
            </select>
          );
        },
      },
      {
        id: "templateFilters",
        label: "Subitem Filters",
        field: "templateFilters",
        width: 220,
        minWidth: 200,
        maxWidth: 280,
        sortable: false,
        resizable: true,
        contentType: "custom",
        render: (value, row) => (
          <MultiSelect
            options={commonFilters}
            selected={value || []}
            onChange={(selectedFilters) =>
              handleFieldChange(row.id, "templateFilters", selectedFilters)
            }
            placeholder="Select filters..."
            size="compact"
          />
        ),
      },
      {
        id: "actions",
        label: "Actions",
        field: "id",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        sortable: false,
        resizable: false,
        align: "center",
        contentType: "icon",
        render: (value, row) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTemplate(row);
            }}
            style={{
              padding: "8px",
              background: "none",
              border: "none",
              color: "var(--gray-400)",
              cursor: "pointer",
              borderRadius: "8px",
              transition: "background-color 0.2s, color 0.2s, transform 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                "var(--danger-bg, rgba(239, 68, 68, 0.1))";
              e.currentTarget.style.color = "var(--danger)";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "var(--gray-400)";
              e.currentTarget.style.transform = "scale(1)";
            }}
            title="Delete template"
          >
            <Trash2 size={20} />
          </button>
        ),
      },
    ],
    [commonPhases, commonFilters, handleFieldChange],
  );

  if (loading) {
    return <LoadingSpinner message="Loading subitem templates..." />;
  }

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "var(--dark-bg)",
        minHeight: "100vh",
      }}
    >
      {/* Error Message */}
      {error && (
        <div style={{ marginBottom: "var(--space-4)" }}>
          <ErrorMessage
            message={error}
            onRetry={loadTemplates}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Page Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "var(--space-6)",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: "var(--text-3xl)",
              fontWeight: "var(--font-bold)",
              color: "var(--gray-50)",
            }}
          >
            Subitem Templates
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: "var(--space-2)",
              fontSize: "var(--text-sm)",
              color: "var(--gray-400)",
            }}
          >
            {filteredAndSortedTemplates.length} template
            {filteredAndSortedTemplates.length !== 1 ? "s" : ""}
          </p>
        </div>

        <Button variant="primary" size="md" onClick={handleNewTemplate}>
          + Add Subitem
        </Button>
      </div>

      {/* Active Filter Chips */}
      {(filters.phase || filters.templateType) && (
        <div
          style={{
            display: "flex",
            gap: "var(--space-2)",
            flexWrap: "wrap",
            marginBottom: "var(--space-4)",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--gray-400)",
              fontWeight: "var(--font-medium)",
            }}
          >
            Active filters:
          </span>
          {filters.phase && (
            <FilterChip
              label={`Phase: ${filters.phase}`}
              onRemove={() => setFilters({ ...filters, phase: "" })}
            />
          )}
          {filters.templateType && (
            <FilterChip
              label={`Type: ${filters.templateType.charAt(0).toUpperCase() + filters.templateType.slice(1)}`}
              onRemove={() => setFilters({ ...filters, templateType: "" })}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters({ phase: "", templateType: "" })}
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Table with integrated toolbar */}
      <div
        style={{
          border: "1px solid var(--gray-600)",
          borderRadius: "var(--radius-lg)",
          overflowX: "auto", // Only horizontal scroll
          overflowY: "visible", // Allow dropdown to overflow vertically
          maxWidth: "100%",
        }}
      >
        {/* Search Bar and Toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "var(--space-4)",
            backgroundColor: "var(--dark-bg)",
            borderBottom: "1px solid var(--gray-600)",
            gap: "var(--space-4)",
          }}
        >
          <div style={{ width: "300px", flexShrink: 0 }}>
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search templates..."
              size="sm"
            />
          </div>
          <div
            style={{
              display: "flex",
              gap: "var(--space-3)",
              alignItems: "center",
            }}
          >
            <Popover
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={16} />
                  Filters
                </Button>
              }
              position="bottom-right"
              width="400px"
              maxWidth="90vw"
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-4)",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "var(--text-base)",
                    fontWeight: "var(--font-semibold)",
                    color: "var(--gray-200)",
                  }}
                >
                  Filter Templates
                </h3>

                {/* Phase Filter */}
                <Dropdown
                  label="Phase"
                  value={filters.phase}
                  options={[
                    { value: "", label: "All phases" },
                    ...commonPhases.map((phase) => ({
                      value: phase,
                      label: phase,
                    })),
                  ]}
                  onChange={(value) => setFilters({ ...filters, phase: value })}
                  placeholder="All phases"
                  size="md"
                />

                {/* Template Type Filter */}
                <Dropdown
                  label="Template Type"
                  value={filters.templateType}
                  options={[
                    { value: "", label: "All types" },
                    { value: "base", label: "Base" },
                    { value: "revision", label: "Revision" },
                    { value: "both", label: "Both" },
                  ]}
                  onChange={(value) =>
                    setFilters({ ...filters, templateType: value })
                  }
                  placeholder="All types"
                  size="md"
                />

                {/* Footer Actions */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingTop: "var(--space-3)",
                    borderTop: "1px solid var(--gray-700)",
                  }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilters({ phase: "", templateType: "" })}
                  >
                    Clear all
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowFilters(false)}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </Popover>

            <Button
              variant={autoResizeEnabled ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
            >
              {autoResizeEnabled ? "✓ " : ""}Auto-resize
            </Button>
          </div>
        </div>

        <Table
          data={displayedTemplates}
          columns={columns}
          sortable={true}
          searchable={false}
          resizable={true}
          paginated={false}
          loading={loading}
          storageKey="subitem-templates-table"
          autoResizeOnDataChange={autoResizeEnabled}
          showAutoSizeButton={false}
          onManualResize={handleManualResize}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          emptyMessage={
            searchTerm || filters.phase || filters.templateType
              ? "No templates match your search or filters."
              : 'No templates found. Click "+ Add Subitem" to create your first template!'
          }
          variant="dense"
          stickyHeader={false} // Disabled sticky header to fix dropdown scrolling issues
          style={{ border: "none", borderRadius: "0", overflow: "visible" }}
        />

        {/* Custom Pagination */}
        {filteredAndSortedTemplates.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "var(--space-4)",
              backgroundColor: "var(--dark-bg)",
              borderTop: "1px solid var(--gray-600)",
              gap: "var(--space-4)",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-3)",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{ fontSize: "var(--text-sm)", color: "var(--gray-400)" }}
              >
                {pageSize === 999999
                  ? `Showing all ${filteredAndSortedTemplates.length} template${filteredAndSortedTemplates.length !== 1 ? "s" : ""}`
                  : `Page ${currentPage} of ${totalPages} (${filteredAndSortedTemplates.length} template${filteredAndSortedTemplates.length !== 1 ? "s" : ""})`}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-2)",
                }}
              >
                <span
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-400)",
                  }}
                >
                  Show:
                </span>
                <Button
                  variant={pageSize === 20 ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => handlePageSizeChange(20)}
                >
                  20
                </Button>
                <Button
                  variant={pageSize === 50 ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => handlePageSizeChange(50)}
                >
                  50
                </Button>
                <Button
                  variant={pageSize === 999999 ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => handlePageSizeChange(999999)}
                >
                  All
                </Button>
              </div>
            </div>

            {totalPages > 1 && (
              <div style={{ display: "flex", gap: "var(--space-2)" }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, currentPage + 2);

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? "secondary" : "ghost"}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </Button>,
                    );
                  }

                  return pages;
                })()}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, template: null })}
        onConfirm={confirmDelete}
        title="Delete Template"
        message={`Are you sure you want to delete "${deleteConfirm.template?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={loading}
      />
    </div>
  );
};

export default SubitemTemplateTable;
